import Ajv from 'ajv';
import { z } from 'zod';
import { OpportunitiesConst } from '@axiom/const';
import { ZodArrayToEnum } from '@axiom/types';

import { EnvoyCandidateSearchFiltersSchema } from './envoy-candidate-search-filters';
import { axiomValidationOptions } from './options';
import { OpportunityStageCodes } from './opportunity';
import { TaxonomyPracticeAreaSchema } from './taxonomy';

export const UserSearchTypes = {
  candidate: 'candidate',
  opportunity: 'opportunity',
};

const UserSearchTypeValues = Object.keys(UserSearchTypes) as Array<
  keyof typeof UserSearchTypes
>;

export const UserSearchSchema = z.object({
  alertActive: z.boolean(),
  id: z.string().uuid(),
  name: z.string().max(255),
  type: z.enum(ZodArrayToEnum(UserSearchTypeValues)),
  userId: z.string().uuid().nullable(),
});

export const UserCandidateSearchSchema = UserSearchSchema.pick({
  alertActive: true,
  id: true,
  name: true,
  type: true,
  userId: true,
}).extend({
  search: EnvoyCandidateSearchFiltersSchema,
});

export const UserOpportunitySearchSchema = UserSearchSchema.pick({
  alertActive: true,
  id: true,
  name: true,
  type: true,
  userId: true,
}).extend({
  search: z.object({
    search: z.string().nullish(),
    stageCode: z.enum(ZodArrayToEnum(OpportunityStageCodes)),
    businessTeam: z
      .enum(ZodArrayToEnum(Object.values(OpportunitiesConst.BusinessTeams)))
      .nullish(),
    ownerUserId: z.string().nullish(),
    salesLeadId: z.string().nullish(),
    practiceAreas: z.array(TaxonomyPracticeAreaSchema),
    closeDate: z.object({
      start: z.string(),
      end: z.string(),
    }),
  }),
});

export type UserSearch = z.infer<typeof UserSearchSchema>;
export type UserCandidateSearch = z.infer<typeof UserCandidateSearchSchema>;
export type UserOpportunitySearch = z.infer<typeof UserOpportunitySearchSchema>;

const ajv = new Ajv(axiomValidationOptions());
const TYPE_ENUM_VALS = ['candidate', 'opportunity'];

const userSearchCommonValidation = {
  name: { type: 'string' },
  type: { type: 'string', enum: TYPE_ENUM_VALS },
  // TODO: identify search properties
  search: { type: 'object' },
};

const createUserSearchValidation = {
  type: 'object',
  additionalProperties: false,
  properties: userSearchCommonValidation,
  required: ['name', 'type', 'search'],
};

const updateUserSearchValidation = {
  type: 'object',
  additionalProperties: false,
  properties: userSearchCommonValidation,
  anyOf: [
    { required: ['name'] },
    { required: ['type'] },
    { required: ['search'] },
  ],
};

const userSearchValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    type: {
      type: 'string',
      enum: TYPE_ENUM_VALS,
    },
  },
};

export const createUserSearchValidator = ajv.compile(
  createUserSearchValidation
);

export const updateUserSearchValidator = ajv.compile(
  updateUserSearchValidation
);

export const userSearchValidator = ajv.compile(userSearchValidation);
